import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";

import { cn } from "../lib/utils";

const buttonVariants = cva(
  // `border border-transparent bg-transparent` — projekt ma wyłączony preflight Tailwinda,
  // więc bez tego <button> dziedziczy domyślne tło/obramowanie przeglądarki (warianty ghost/link).
  "inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-crane-md border border-transparent bg-transparent text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-crane-ring focus-visible:ring-offset-2 focus-visible:ring-offset-crane-background disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-crane-primary text-crane-primary-foreground shadow-crane-soft hover:bg-crane-primary/90",
        destructive:
          "bg-crane-destructive text-crane-destructive-foreground shadow-crane-soft hover:bg-crane-destructive/90",
        outline:
          "border border-crane-border bg-crane-background hover:bg-crane-accent hover:text-crane-accent-foreground",
        secondary: "bg-crane-secondary text-crane-secondary-foreground shadow-crane-soft hover:bg-crane-secondary/80",
        ghost: "hover:bg-crane-accent hover:text-crane-accent-foreground",
        link: "text-crane-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-crane-md px-3 text-xs",
        lg: "h-10 rounded-crane-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /** Operacja uruchomiona tym przyciskiem trwa: spinner przed treścią, przycisk
   *  zablokowany i `aria-busy`. Blokada jest częścią kontraktu, nie ozdobą — bez niej
   *  drugie kliknięcie „Zapisz" wysyła to samo żądanie po raz drugi.
   */
  loading?: boolean;
  loadingLabel?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, loadingLabel, disabled, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";

    const blocked = disabled || loading || undefined;

    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, className }),
          asChild && "aria-disabled:pointer-events-none aria-disabled:opacity-50",
        )}
        ref={ref}
        disabled={blocked}
        aria-disabled={asChild ? blocked : undefined}
        aria-busy={loading || undefined}
        {...props}
      >
        {asChild ? (
          children
        ) : (
          <>
            {loading ? <Loader2 className="animate-spin" aria-hidden /> : null}
            {loading && loadingLabel !== undefined ? loadingLabel : children}
          </>
        )}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
